const MAX_OUTPUT_BYTES = 45 * 1024 * 1024
const ENCODE_BUDGET_BYTES = 43 * 1024 * 1024
const MIN_ENCODE_BUDGET_BYTES = 512 * 1024
const AUDIO_BITRATE_KBPS = 96
const MIN_VIDEO_BITRATE_KBPS = 120
const MAX_VIDEO_BITRATE_KBPS = 2500
const FFMPEG_CORE_VERSION = "0.12.10"
const VIDEO_FILTER = "scale=w='if(gte(iw,ih),min(1280,iw),min(720,iw))':h='if(gte(iw,ih),min(720,ih),min(1280,ih))':force_original_aspect_ratio=decrease:force_divisible_by=2,fps=30"

export class VideoCompressionError extends Error {
  constructor(code, cause = null) {
    super(code)
    this.name = "VideoCompressionError"
    this.code = code
    this.cause = cause
  }
}

function clamp(value, minimum, maximum) {
  return Math.min(maximum, Math.max(minimum, value))
}

function outputName() {
  const random = typeof crypto !== "undefined" && typeof crypto.randomUUID === "function"
    ? crypto.randomUUID().replace(/-/g, "")
    : `${Date.now()}-${Math.random().toString(16).slice(2)}`
  return `starlive-compressed-${random}.mp4`
}

function safeWorkerFile(file) {
  const extension = String(file.name || "").match(/\.[a-z0-9]{1,8}$/i)
  return new File([file], `starlive-input${extension ? extension[0].toLowerCase() : ".video"}`, {
    type: file.type || "application/octet-stream",
    lastModified: file.lastModified || Date.now()
  })
}

function bitrateFor(duration, budgetBytes = ENCODE_BUDGET_BYTES) {
  const totalKbps = budgetBytes * 8 / Math.max(0.1, duration) / 1000
  return Math.round(clamp(totalKbps - AUDIO_BITRATE_KBPS - 24, MIN_VIDEO_BITRATE_KBPS, MAX_VIDEO_BITRATE_KBPS))
}

function coreAssetUrl(filename) {
  const publicPath = String(process.env.BASE_URL || "/").replace(/\/?$/, "/")
  return new URL(`${publicPath}vendor/ffmpeg/${FFMPEG_CORE_VERSION}/${filename}`, window.location.origin).href
}

function readVideoDuration(file) {
  return new Promise((resolve, reject) => {
    const video = document.createElement("video")
    const sourceUrl = URL.createObjectURL(file)
    let timer = null
    const cleanup = () => {
      window.clearTimeout(timer)
      video.removeEventListener("loadedmetadata", onLoaded)
      video.removeEventListener("error", onError)
      video.removeAttribute("src")
      URL.revokeObjectURL(sourceUrl)
    }
    const onLoaded = () => {
      const duration = Number(video.duration)
      cleanup()
      if (!Number.isFinite(duration) || duration <= 0) reject(new VideoCompressionError("invalid_duration"))
      else resolve(duration)
    }
    const onError = () => { cleanup(); reject(new VideoCompressionError("invalid_video")) }
    video.preload = "metadata"
    video.muted = true
    video.playsInline = true
    video.addEventListener("loadedmetadata", onLoaded, { once: true })
    video.addEventListener("error", onError, { once: true })
    timer = window.setTimeout(() => { cleanup(); reject(new VideoCompressionError("invalid_duration")) }, 15000)
    video.src = sourceUrl
    video.load()
  })
}

function encodeArguments(inputPath, outputPath, videoBitrateKbps) {
  return [
    "-hide_banner", "-y",
    "-i", inputPath,
    "-map", "0:v:0",
    "-map", "0:a?",
    "-vf", VIDEO_FILTER,
    "-c:v", "libx264",
    "-preset", "ultrafast",
    "-pix_fmt", "yuv420p",
    "-b:v", `${videoBitrateKbps}k`,
    "-maxrate", `${videoBitrateKbps}k`,
    "-bufsize", `${videoBitrateKbps * 2}k`,
    "-c:a", "aac",
    "-b:a", `${AUDIO_BITRATE_KBPS}k`,
    "-ac", "2",
    "-ar", "44100",
    "-movflags", "+faststart",
    "-map_metadata", "-1",
    outputPath
  ]
}

/**
 * 在浏览器 Worker 内转码。输入通过 WORKERFS 挂载，避免复制数百 MiB 原文件。
 * 返回值始终是已验证不超过 45 MiB 的 MP4；任何失败都不回退原文件。
 */
export async function compressVideoFile(file, onProgress = null) {
  if (!(file instanceof File) || !/^video\//i.test(file.type || "")) {
    throw new VideoCompressionError("invalid_video")
  }
  if (typeof WebAssembly === "undefined" || typeof Worker === "undefined") {
    throw new VideoCompressionError("unsupported_environment")
  }

  let ffmpeg = null
  let inputDirectory = ""
  let outputPath = ""
  let mounted = false
  const notify = (stage, progress) => {
    if (typeof onProgress === "function") onProgress({ stage, progress: clamp(Math.round(progress), 0, 100) })
  }

  try {
    notify("probing", 0)
    const duration = await readVideoDuration(file)
    notify("loading", 1)
    const { FFmpeg } = await import(/* webpackChunkName: "video-compressor" */ "@ffmpeg/ffmpeg")
    ffmpeg = new FFmpeg()
    await ffmpeg.load({
      coreURL: coreAssetUrl("ffmpeg-core.js"),
      wasmURL: coreAssetUrl("ffmpeg-core.wasm")
    })

    const taskId = `${Date.now()}-${Math.random().toString(16).slice(2)}`
    inputDirectory = `/input-${taskId}`
    outputPath = `/${outputName()}`
    await ffmpeg.createDir(inputDirectory)
    const workerFile = safeWorkerFile(file)
    await ffmpeg.mount("WORKERFS", { files: [workerFile] }, inputDirectory)
    mounted = true
    const inputPath = `${inputDirectory}/${workerFile.name}`
    const targetOutputBytes = Math.min(MAX_OUTPUT_BYTES, Math.max(MIN_ENCODE_BUDGET_BYTES, Math.floor(file.size * 0.95)))
    const initialBudgetBytes = Math.min(ENCODE_BUDGET_BYTES, Math.max(MIN_ENCODE_BUDGET_BYTES, Math.floor(file.size * 0.82)))
    let videoBitrateKbps = bitrateFor(duration, initialBudgetBytes)
    let compressedFile = null

    for (let attempt = 0; attempt < 2; attempt += 1) {
      const progressStart = attempt === 0 ? 2 : 80
      const progressSpan = attempt === 0 ? 76 : 18
      const progressHandler = ({ progress }) => notify("compressing", progressStart + clamp(Number(progress) || 0, 0, 1) * progressSpan)
      ffmpeg.on("progress", progressHandler)
      const exitCode = await ffmpeg.exec(encodeArguments(inputPath, outputPath, videoBitrateKbps))
      ffmpeg.off("progress", progressHandler)
      if (exitCode !== 0) throw new VideoCompressionError("compression_failed")

      const output = await ffmpeg.readFile(outputPath)
      if (!(output instanceof Uint8Array) || output.byteLength <= 0) {
        throw new VideoCompressionError("compression_failed")
      }
      compressedFile = new File([output], outputPath.slice(1), { type: "video/mp4", lastModified: Date.now() })
      if (compressedFile.size <= targetOutputBytes) break
      if (attempt === 0) {
        videoBitrateKbps = Math.max(
          MIN_VIDEO_BITRATE_KBPS,
          Math.floor(videoBitrateKbps * targetOutputBytes / compressedFile.size * 0.9)
        )
        compressedFile = null
        await ffmpeg.deleteFile(outputPath)
      }
    }

    if (!compressedFile || compressedFile.size > MAX_OUTPUT_BYTES || (file.size >= MIN_ENCODE_BUDGET_BYTES && compressedFile.size >= file.size)) {
      throw new VideoCompressionError("too_large")
    }
    notify("complete", 100)
    return { file: compressedFile, duration, originalSize: file.size, compressedSize: compressedFile.size }
  } catch (error) {
    if (error instanceof VideoCompressionError) throw error
    throw new VideoCompressionError("compression_failed", error)
  } finally {
    if (ffmpeg) {
      if (outputPath) {
        try { await ffmpeg.deleteFile(outputPath) } catch (_) {}
      }
      if (mounted && inputDirectory) {
        try { await ffmpeg.unmount(inputDirectory) } catch (_) {}
      }
      if (inputDirectory) {
        try { await ffmpeg.deleteDir(inputDirectory) } catch (_) {}
      }
      ffmpeg.terminate()
    }
  }
}

export { MAX_OUTPUT_BYTES }
